import { motion, useScroll, useTransform } from 'framer-motion';
import { useRef } from 'react';
import { ASSETS } from '@/lib/assets';

export default function Ritual() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ['start end', 'end start'] });
  const y = useTransform(scrollYProgress, [0, 1], [80, -80]);
  const scale = useTransform(scrollYProgress, [0, 0.5, 1], [1.05, 1, 1.05]);

  return (
    <section
      id="ritual"
      ref={ref}
      className="relative bg-[#FAF8F4] overflow-hidden py-32 lg:py-48"
    >
      <div className="max-w-[1400px] mx-auto px-6 lg:px-12 grid grid-cols-1 lg:grid-cols-12 gap-16 items-center">
        {/* Lifestyle photo */}
        <motion.div
          style={{ y }}
          className="lg:col-span-6 order-2 lg:order-1 relative aspect-[4/5] lg:aspect-auto lg:h-[70vh] rounded-2xl overflow-hidden"
        >
          <motion.div
            style={{ scale }}
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true, margin: '-120px' }}
            transition={{ duration: 1.4, ease: [0.22, 1, 0.36, 1] }}
            className="absolute inset-0 bg-cover bg-center"
          >
            <img
              src={ASSETS.heroStoneGreyDesk}
              alt="KIVO Stone Grey tumbler on a morning desk"
              loading="lazy"
              className="absolute inset-0 w-full h-full object-cover"
            />
          </motion.div>

          {/* Caption */}
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, delay: 0.4 }}
            className="absolute bottom-5 left-5 right-5 flex items-end justify-between text-white"
          >
            <div>
              <div className="text-[10px] tracking-[0.3em] uppercase opacity-80">
                Frame 02 · The Morning
              </div>
              <div className="font-serif italic text-xl mt-1">A 7:24 a.m. pause.</div>
            </div>
            <div className="text-[10px] tracking-[0.3em] uppercase opacity-80">KIVO</div>
          </motion.div>
        </motion.div>

        {/* Text */}
        <div className="lg:col-span-6 order-1 lg:order-2 lg:pl-8">
          <motion.div
            initial={{ opacity: 0, y: 14 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.9 }}
            className="text-[11px] tracking-[0.35em] uppercase text-[#CBBBA0] mb-6"
          >
            Chapter 02 · The Ritual
          </motion.div>

          <motion.h2
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 1 }}
            className="font-display text-[clamp(2.5rem,7vw,6rem)] leading-[0.95] tracking-[-0.03em] text-[#111]"
          >
            <motion.span
              initial={{ y: 30, opacity: 0 }}
              whileInView={{ y: 0, opacity: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.9, delay: 0.1 }}
              className="block"
            >
              One Sip.
            </motion.span>
            <motion.span
              initial={{ y: 30, opacity: 0 }}
              whileInView={{ y: 0, opacity: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.9, delay: 0.25 }}
              className="block"
            >
              One Pause.
            </motion.span>
            <motion.span
              initial={{ y: 30, opacity: 0 }}
              whileInView={{ y: 0, opacity: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.9, delay: 0.4 }}
              className="block font-serif italic text-[#CBBBA0]"
            >
              One Better Day.
            </motion.span>
          </motion.h2>

          <motion.p
            initial={{ opacity: 0, y: 14 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.9, delay: 0.55 }}
            className="mt-10 max-w-md text-[17px] leading-relaxed text-[#111]/65"
          >
            KIVO transforms hydration from a task into a daily ritual — a small,
            deliberate moment of care woven into the noise of modern life.
          </motion.p>

          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 1, delay: 0.8 }}
            className="mt-10 flex items-center gap-6 text-[11px] tracking-[0.25em] uppercase text-[#111]/50"
          >
            <span>700ml</span>
            <span className="h-px w-8 bg-[#111]/20" />
            <span>Double-Wall Insulated</span>
            <span className="h-px w-8 bg-[#111]/20 hidden md:block" />
            <span className="hidden md:inline">304 Steel</span>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
